import React, { useState } from "react";
import { useAction } from "wasp/client/operations";
import { searchUsers, resetUserUsage } from "wasp/client/operations";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

interface UserWithUsage {
  id: string;
  email: string;
  subscriptionTier: string;
  plansUsed: number;
  swapsUsed: number;
  planLimit: number;
  swapLimit: number;
}

export const UsageManagementTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<UserWithUsage[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const doSearchUsers = useAction(searchUsers);
  const doResetUsage = useAction(resetUserUsage);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    try {
      const results = await doSearchUsers({ query: searchTerm });
      setUsers(results as UserWithUsage[]);
    } catch (error: any) {
      toast.error("Failed to search users: " + error.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResetUsage = async (userId: string, resetType: string) => {
    try {
      await doResetUsage({ userId, resetType });
      toast.success("User usage reset successfully");

      // Refresh the search results
      if (searchTerm) {
        await handleSearch();
      }
    } catch (error: any) {
      toast.error("Failed to reset usage: " + error.message);
    }
  };

  const getUsageColor = (used: number, limit: number) => {
    const percentage = (used / limit) * 100;
    if (percentage >= 90) return "destructive";
    if (percentage >= 75) return "secondary";
    return "default";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>User Usage Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6 flex gap-4">
            <Input
              placeholder="Search by email or user ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? "Searching..." : "Search"}
            </Button>
          </div>

          {users.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                Search Results ({users.length})
              </h3>
              <div className="grid gap-4">
                {users.map((user) => (
                  <Card key={user.id}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{user.email}</span>
                            <Badge variant="outline">
                              {user.subscriptionTier}
                            </Badge>
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground">
                            <span>
                              Plans: {user.plansUsed}/{user.planLimit}
                            </span>
                            <span>
                              Swaps: {user.swapsUsed}/{user.swapLimit}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetUsage(user.id, "weekly")}
                          >
                            Reset Weekly
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleResetUsage(user.id, "all")}
                          >
                            Reset All
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {users.length === 0 && searchTerm && !isSearching && (
            <p className="py-8 text-center text-muted-foreground">
              No users found matching "{searchTerm}"
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
